import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Skeleton,
} from '@mui/material';
import {
  Email as EmailIcon,
  People as PeopleIcon,
  Campaign as CampaignIcon,
  DoNotDisturb as UnsubscribeIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  OpenInNew as OpenIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { marketingApi } from '../../api/marketing';
import type { MarketCampaign, MarketAudience } from '../../api/marketing';

interface DashboardStats {
  totalContacts: number;
  totalAudiences: number;
  totalCampaigns: number;
  totalUnsubscribers: number;
  recentCampaigns: MarketCampaign[];
  activeAudiences: MarketAudience[];
}

const MarketingDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    totalContacts: 0,
    totalAudiences: 0,
    totalCampaigns: 0,
    totalUnsubscribers: 0,
    recentCampaigns: [],
    activeAudiences: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [users, audiences, campaigns, unsubscribers] = await Promise.all([
        marketingApi.getUsers({ page_size: 1 }),
        marketingApi.getAudiences({ is_active: true }),
        marketingApi.getCampaigns({ page_size: 5 }),
        marketingApi.getUnsubscribers({ page_size: 1, is_active: true }),
      ]);

      setStats({
        totalContacts: users.count,
        totalAudiences: audiences.count,
        totalCampaigns: campaigns.count,
        totalUnsubscribers: unsubscribers.count,
        recentCampaigns: campaigns.results,
        activeAudiences: audiences.results.slice(0, 5),
      });
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const getStatusColor = (status: string) => {
    const colors: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
      draft: 'default',
      scheduled: 'primary',
      sending: 'warning',
      sent: 'success',
      failed: 'error',
      paused: 'warning',
    };
    return colors[status] || 'default';
  };

  const StatCard: React.FC<{
    title: string;
    value: number;
    icon: React.ReactNode;
    color: string;
    onClick?: () => void;
  }> = ({ title, value, icon, color, onClick }) => (
    <Card
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { transform: 'translateY(-2px)', transition: 'all 0.3s' } : {},
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" component="h2">
              {loading ? <Skeleton width={80} /> : value.toLocaleString()}
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: color,
              borderRadius: '50%',
              width: 56,
              height: 56,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Marketing Dashboard
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<HelpIcon />}
            onClick={() => navigate('/marketing/docs')}
            sx={{ mr: 1 }}
          >
            帮助文档
          </Button>
          <IconButton onClick={fetchDashboardData} color="primary">
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<CampaignIcon />}
            onClick={() => navigate('/marketing/campaigns/create')}
            sx={{ ml: 2 }}
          >
            Create Campaign
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Grid */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Contacts"
            value={stats.totalContacts}
            icon={<PeopleIcon />}
            color="#2196f3"
            onClick={() => navigate('/marketing/contacts')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Audiences"
            value={stats.totalAudiences}
            icon={<PeopleIcon />}
            color="#4caf50"
            onClick={() => navigate('/marketing/audiences')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Campaigns"
            value={stats.totalCampaigns}
            icon={<EmailIcon />}
            color="#ff9800"
            onClick={() => navigate('/marketing/campaigns')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Unsubscribers"
            value={stats.totalUnsubscribers}
            icon={<UnsubscribeIcon />}
            color="#f44336"
            onClick={() => navigate('/marketing/unsubscribers')}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Recent Campaigns */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Recent Campaigns</Typography>
              <Button
                size="small"
                endIcon={<OpenIcon />}
                onClick={() => navigate('/marketing/campaigns')}
              >
                View All
              </Button>
            </Box>

            {loading ? (
              <Skeleton variant="rectangular" height={300} />
            ) : stats.recentCampaigns.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Campaign</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Sent</TableCell>
                      <TableCell align="center">Opens</TableCell>
                      <TableCell align="center">Clicks</TableCell>
                      <TableCell>Progress</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.recentCampaigns.map((campaign) => (
                      <TableRow key={campaign.id}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {campaign.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {campaign.subject}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={campaign.status}
                            size="small"
                            color={getStatusColor(campaign.status)}
                          />
                        </TableCell>
                        <TableCell align="center">{campaign.sent_count}</TableCell>
                        <TableCell align="center">
                          {campaign.opened_count}
                          {campaign.sent_count > 0 && (
                            <Typography variant="caption" display="block" color="textSecondary">
                              {((campaign.opened_count / campaign.sent_count) * 100).toFixed(1)}%
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {campaign.clicked_count}
                          {campaign.sent_count > 0 && (
                            <Typography variant="caption" display="block" color="textSecondary">
                              {((campaign.clicked_count / campaign.sent_count) * 100).toFixed(1)}%
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <LinearProgress
                              variant="determinate"
                              value={campaign.progress}
                              sx={{ width: 100, mr: 1 }}
                            />
                            <Typography variant="caption">{campaign.progress}%</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/marketing/campaigns/${campaign.id}`)}
                          >
                            <OpenIcon />
                          </IconButton>
                          {campaign.can_send && (
                            <IconButton size="small" color="primary">
                              <SendIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box textAlign="center" py={4}>
                <Typography color="textSecondary">No campaigns yet</Typography>
                <Button
                  variant="outlined"
                  startIcon={<CampaignIcon />}
                  onClick={() => navigate('/marketing/campaigns/create')}
                  sx={{ mt: 2 }}
                >
                  Create Your First Campaign
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Active Audiences */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Active Audiences</Typography>
              <Button
                size="small"
                endIcon={<OpenIcon />}
                onClick={() => navigate('/marketing/audiences')}
              >
                View All
              </Button>
            </Box>

            {loading ? (
              <Skeleton variant="rectangular" height={250} />
            ) : stats.activeAudiences.length > 0 ? (
              <Box>
                {stats.activeAudiences.map((audience) => (
                  <Card
                    key={audience.id}
                    variant="outlined"
                    sx={{
                      mb: 2,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                    onClick={() => navigate(`/marketing/audiences/${audience.id}`)}
                  >
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="subtitle2">{audience.name}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {audience.member_count} members
                            {audience.eligible_count < audience.member_count && (
                              <> ({audience.eligible_count} eligible)</>
                            )}
                          </Typography>
                        </Box>
                        {audience.needs_rebuild && (
                          <Chip label="Needs Rebuild" size="small" color="warning" />
                        )}
                      </Box>
                      {audience.last_rebuilt && (
                        <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                          Last rebuilt: {new Date(audience.last_rebuilt).toLocaleDateString()}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              <Box textAlign="center" py={4}>
                <Typography color="textSecondary">No audiences yet</Typography>
                <Button
                  variant="outlined"
                  startIcon={<PeopleIcon />}
                  onClick={() => navigate('/marketing/audiences/create')}
                  sx={{ mt: 2 }}
                >
                  Create Audience
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MarketingDashboard;